import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, GraduationCap, Users, UserRound,
  ScrollText, Settings, LogOut, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, exact: true },
  { to: "/admin/ecoles", label: "Écoles", icon: Building2 },
  { to: "/admin/enseignants", label: "Enseignants", icon: GraduationCap },
  { to: "/admin/parents", label: "Parents", icon: Users },
  { to: "/admin/eleves", label: "Élèves", icon: UserRound },
  { to: "/admin/journal", label: "Journal d'audit", icon: ScrollText },
  { to: "/admin/parametres", label: "Paramètres", icon: Settings },
];

export function AdminShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <div className="min-h-screen w-full bg-[#F5F6F8] text-slate-900">
      {/* Barre latérale */}
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-64 flex-col bg-[#0B1120] text-slate-300 md:flex">
        <div className="flex items-center gap-3 border-b border-white/10 px-6 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-500/20 text-indigo-300">
            <ShieldCheck size={20} />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Administration</p>
            <p className="text-xs text-slate-400">Le Passeport</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {NAV.map((item) => {
            const active = isActive(item);
            return (
              <button
                key={item.to}
                onClick={() => navigate(item.to)}
                data-testid={`nav-${item.to.replace(/\//g, "-")}`}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/20"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                <item.icon size={18} /> {item.label}
              </button>
            );
          })}
        </nav>
        <div className="border-t border-white/10 px-3 py-4">
          <div className="mb-2 px-3 text-xs text-slate-400">
            {user?.first_name} {user?.last_name}
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
          >
            <LogOut size={18} /> Se déconnecter
          </button>
        </div>
      </aside>

      {/* Nav mobile */}
      <div className="sticky top-0 z-20 flex items-center gap-2 overflow-x-auto border-b border-slate-200 bg-[#0B1120] px-3 py-2 md:hidden">
        {NAV.map((item) => {
          const active = isActive(item);
          return (
            <button
              key={item.to}
              onClick={() => navigate(item.to)}
              className={`flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium ${
                active ? "bg-indigo-500 text-white" : "text-slate-400"
              }`}
            >
              <item.icon size={14} /> {item.label}
            </button>
          );
        })}
        <button
          onClick={logout}
          data-testid="admin-logout-mobile"
          className="flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-rose-300"
        >
          <LogOut size={14} /> Se déconnecter
        </button>
      </div>

      <main className="md:pl-64">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8 sm:py-10">{children}</div>
      </main>
    </div>
  );
}
